/**
 * NutritionMySessionsPage — the FOLIAR operator's worklist (spec §4.6).
 *
 * FOLIAR-ONLY. Soil operators use My Soil Applications (/nutrition/soil-groups,
 * live since U5). The server stopped returning soil rows in F5, but the Dexie
 * cache SURVIVES the deploy and refreshMyNutritionSessions only clears it on a
 * SUCCESSFUL fetch — so an operator opening the app offline after upgrading
 * still holds yesterday's soil rows, which would navigate to a page F6 deletes.
 * isForeignWorkItem() filters them out.
 *
 * STYLING: `.ntr-fol-*`, a NEW namespace. `.ntr-my-session-*` was shared with
 * SoilMyGroupsPage, and no test in this repo can see a CSS change — restyling
 * it would silently alter soil (spec §7.1).
 *
 * Mounted behind <RequireLinkedOperator> + <RequireMenu fertilizing:mysessions>.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db';
import type { NutritionMyWorkItem } from '../../db';
import { refreshMyNutritionSessions } from '../../services/nutritionOperatorSessionCacheService';
import {
  resetNutritionComboSyncState, syncPendingNutritionCombos,
  readPendingFoliarComboCount, readPendingFoliarComboKeys,
} from '../../services/nutritionOperatorSessionSyncService';
import PageHeader from '../shell/PageHeader';
import { RowCard, StatusPill } from '../patterns';
import { formatMeasure } from '../../utils/formatMeasure';
import { isForeignWorkItem } from './foliarOperator';
import FoliarSyncBanner from './FoliarSyncBanner';
import { SESSION_STATUS_VARIANT } from './nutritionSessionStatus';
import './nutrition.css';

const NutritionMySessionsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingKeys, setPendingKeys] = useState<string[]>([]);
  const [online, setOnline] = useState<boolean>(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  const items: NutritionMyWorkItem[] = (
    (useLiveQuery(() => db.nutritionMySessions.toArray(), []) as NutritionMyWorkItem[] | undefined) ?? []
  ).filter((item) => !isForeignWorkItem(item));

  const loadPending = useCallback(async () => {
    const [count, keys] = await Promise.all([
      readPendingFoliarComboCount(),
      readPendingFoliarComboKeys(),
    ]);
    setPendingCount(count);
    setPendingKeys(keys);
  }, []);

  useEffect(() => {
    // Recover anything stuck mid-flight, drain, refresh, then read whatever the
    // drain could not clear. Each step is failure-tolerant so an offline
    // operator still gets a rendered page.
    resetNutritionComboSyncState()
      .then(() => syncPendingNutritionCombos())
      .catch(() => {})
      .then(() => refreshMyNutritionSessions())
      .catch(() => {})
      .then(() => loadPending())
      .catch(() => {});
    // `t` from react-i18next is not referentially stable here; omit it deliberately
    // rather than "satisfying" the linter with an ever-changing dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPending]);

  useEffect(() => {
    // The banner is the operator's only signal that work is being held locally,
    // so it must track connectivity live rather than only at mount.
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const handleRowClick = (item: NutritionMyWorkItem) => {
    navigate(`/nutrition/operator-sessions/${item.id}`);
  };

  return (
    <div className="pat-page" data-testid="nutrition-my-sessions-page">
      <PageHeader title={t('nutrition.mySessions.title')} />

      <FoliarSyncBanner online={online} pendingCount={pendingCount} />

      {items.length === 0 ? (
        <div className="ntr-empty">
          <div className="ntr-empty__message">{t('nutrition.mySessions.empty')}</div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-3 mt-3">
          {items.map((item) => {
            // es5 target: indexOf, never Array.prototype.includes.
            const queued = pendingKeys.indexOf(item.id) !== -1;
            // `??`, not `||` — a comboStatus of '' must not fall through to status.
            const statusKey = item.comboStatus ?? item.status;
            return (
              <div
                key={item.id}
                data-testid={`foliar-work-row-${item.id}`}
                className="ntr-fol-row"
                role="button"
                tabIndex={0}
                onClick={() => handleRowClick(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleRowClick(item);
                }}
              >
                <RowCard queued={queued}>
                  <div className="ntr-fol-card">
                    <div className="ntr-fol-card__identity">
                      {`${item.roundName} · ${item.orchardOrGroup}`}
                    </div>
                    <div className="ntr-fol-card__meta">{item.programmeName}</div>
                    <div className="ntr-fol-card__facts">
                      {`${item.cultivar ?? '—'} · ${formatMeasure(item.areaHa, 'ha')}`}
                    </div>
                    <div className="ntr-fol-hero" data-testid={`foliar-work-hero-${item.id}`}>
                      {/* formatMeasure with no unit is a plain 2dp number (or an em dash
                          for null); the unit is a separate span so the hero number can
                          carry its own type scale — never pass 'tanks' through as a unit,
                          this is a count, not a measured quantity. */}
                      <span className="ntr-fol-hero__value">{formatMeasure(item.tanksPlanned)}</span>
                      {/* Fix 3 (whole-branch review, human ruling): this hero is the
                          CEILING tank count — "tanks to mix" — distinct from the
                          capture screen's before-start hero, which is the exact
                          fraction ("tanks planned"). Both numbers are kept; only the
                          caption differentiates them. */}
                      <span className="ntr-fol-hero__unit">{t('nutrition.mySessions.tanksToMix')}</span>
                    </div>
                    {queued && (
                      <div className="ntr-fol-card__queued" data-testid={`foliar-work-queued-${item.id}`}>
                        {t('nutrition.mySessions.queuedRow')}
                      </div>
                    )}
                  </div>
                  <div className="ntr-fol-card__rail">
                    <span data-testid={`foliar-work-status-${item.id}`}>
                      <StatusPill variant={SESSION_STATUS_VARIANT[statusKey] ?? 'info'}>
                        {t(`nutrition.sessions.status.${statusKey}`)}
                      </StatusPill>
                    </span>
                  </div>
                </RowCard>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NutritionMySessionsPage;
